import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appointmentSessionsService } from '../services/api';
import surveyService from '../services/surveyService';
import { useAuth } from '../contexts/AuthContext';
import { Clock, Play, Square, Calendar, User, Phone, MapPin, CheckCircle, ExternalLink, Flag } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

const AppointmentSessions = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [notes, setNotes] = useState('');
  const [showComplete3DModal, setShowComplete3DModal] = useState(false);
  const [complete3DSurvey, setComplete3DSurvey] = useState(null);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);

  // Buscar agendamentos do dia
  const { data: appointmentsData, isLoading: isLoadingAppointments } = useQuery({
    queryKey: ['appointment-sessions-today'],
    queryFn: () => appointmentSessionsService.getTodayAppointments(),
    refetchInterval: 30000,
    staleTime: 10000,
  });

  // Buscar sessões de atendimento do dia (para históricos concluídos)
  const { data: sessionsData, isLoading: isLoadingSessions } = useQuery({
    queryKey: ['appointment-sessions-today-sessions'],
    queryFn: () => appointmentSessionsService.getTodaySessions(),
    staleTime: 10000,
  });

  // Buscar sessão atual (em andamento)
  const { data: currentSessionData, isLoading: isLoadingSession } = useQuery({
    queryKey: ['appointment-sessions-current'],
    queryFn: () => appointmentSessionsService.getCurrentSession(),
    refetchInterval: 5000, // Atualizar a cada 5 segundos quando houver sessão ativa
    staleTime: 5000,
  });

  // Normalizar os dados para garantir que seja sempre um array
  const appointments = useMemo(() => {
    if (!appointmentsData) return [];
    const data = appointmentsData.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && Array.isArray(data.data)) return data.data;
    return [];
  }, [appointmentsData]);

  // Normalizar sessões do dia
  const sessions = useMemo(() => {
    if (!sessionsData) return [];
    const data = sessionsData.data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && Array.isArray(data.data)) return data.data;
    return [];
  }, [sessionsData]);

  // Verificar se há sessão aberta (backend: { success, data })
  const currentSession = currentSessionData?.data?.data || null;

  // Identificar agendamento correspondente da sessão atual, caso exista
  const currentAppointment = useMemo(() => {
    if (!currentSession) return null;
    if (currentSession.appointment_id) {
      return appointments.find((a) => a.id === currentSession.appointment_id) || null;
    }
    return null;
  }, [currentSession, appointments]);

  const isCurrentPresentation3D =
    currentSession?.appointment_type === 'PRESENTATION_3D' ||
    currentAppointment?.appointment_type === 'PRESENTATION_3D';

  const currentSurveyId =
    currentSession?.survey_id ||
    currentAppointment?.survey_id ||
    null;

  // Mutação para iniciar sessão
  const startSessionMutation = useMutation({
    mutationFn: (data) => appointmentSessionsService.startSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointment-sessions-current']);
      queryClient.invalidateQueries(['appointment-sessions-today']);
      queryClient.invalidateQueries(['appointments']);
      toast.success('Atendimento iniciado com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao iniciar atendimento');
    },
  });

  // Mutação para finalizar sessão
  const endSessionMutation = useMutation({
    mutationFn: (data) => appointmentSessionsService.endSession(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['appointment-sessions-current']);
      queryClient.invalidateQueries(['appointment-sessions-today']);
      queryClient.invalidateQueries(['appointments']);

      const session = response.data?.data || null;
      if (!session || session.actual_duration_minutes == null) {
        toast.success('Atendimento finalizado com sucesso!');
        setNotes('');
        return;
      }

      const duration = session.actual_duration_minutes;
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      toast.success(`Atendimento finalizado! Duração: ${hours}h ${minutes}min`);
      setNotes('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao finalizar atendimento');
    },
  });

  // Mutação para concluir projeto 3D
  const complete3DMutation = useMutation({
    mutationFn: ({ surveyId }) => surveyService.updateSurvey(surveyId, { project_3d_status: 'COMPLETED' }),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointment-sessions-current']);
      queryClient.invalidateQueries(['appointment-sessions-today']);
      queryClient.invalidateQueries(['appointment-sessions-today-sessions']);
      queryClient.invalidateQueries(['survey']);
      queryClient.invalidateQueries(['surveys']);
      toast.success('Projeto 3D concluído e atendimento finalizado com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao concluir projeto 3D');
    },
  });

  // Calcular tempo decorrido da sessão atual
  const getElapsedTime = () => {
    if (!currentSession || !currentSession.session_start) return null;

    const start = new Date(currentSession.session_start);
    const now = new Date();
    const diff = now - start;
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    return { hours, minutes, seconds };
  };

  const elapsedTime = getElapsedTime();

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (minutes == null) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}min`;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Controle de Atendimentos</h1>
        <p className="text-gray-600">Inicie e finalize seus atendimentos do dia</p>
      </div>

      {/* Sessão Atual em Andamento */}
      {currentSession && currentSession.id && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className={`rounded-lg p-4 border ${
            isCurrentPresentation3D
              ? 'bg-purple-50/70 border-purple-200'
              : currentSession.survey_schedule_id
              ? 'bg-purple-50 border-purple-200'
              : 'bg-[#f0f7f0] border-[#d9ead9]'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-lg font-semibold flex items-center gap-2 ${
                  isCurrentPresentation3D
                    ? 'text-purple-900'
                    : currentSession.survey_schedule_id
                    ? 'text-purple-800'
                    : 'text-[#2c5824]'
                }`}>
                  <Play className="w-5 h-5" />
                  {isCurrentPresentation3D
                    ? '🎨 Apresentação 3D em Andamento'
                    : currentSession.survey_schedule_id
                    ? 'Projeto 3D em Andamento'
                    : 'Atendimento em Andamento'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {currentSession.survey_schedule_id
                    ? `Projeto: ${currentSession.survey_code || 'Sem código'}`
                    : `Cliente: ${currentSession.client_name || currentAppointment?.client_name || '-'}`
                  }
                </p>
                <p className="text-sm text-gray-600">
                  Iniciado às: {formatTime(currentSession.session_start)}
                </p>
                {currentSession.survey_schedule_id && currentSession.survey_id && !isCurrentPresentation3D && (
                  <button
                    onClick={() => navigate(`/levantamentos?surveyId=${currentSession.survey_id}`)}
                    className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 mt-1 underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Ver Levantamento
                  </button>
                )}
                {isCurrentPresentation3D && currentSurveyId && (
                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => navigate(`/levantamentos/${currentSurveyId}/ficha-orcamento`)}
                      className="text-sm text-purple-700 hover:text-purple-900 flex items-center gap-1 font-medium underline"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Ficha de Orçamento
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/levantamentos?surveyId=${currentSurveyId}`)}
                      className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 underline"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Ver Levantamento
                    </button>
                  </div>
                )}
                {!isCurrentPresentation3D && !currentSession.survey_schedule_id && currentSurveyId && (
                  <button
                    type="button"
                    onClick={() => navigate(`/levantamentos?surveyId=${currentSurveyId}`)}
                    className="text-sm text-emerald-700 hover:text-emerald-900 flex items-center gap-1 mt-1 underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Ver Levantamento
                  </button>
                )}
              </div>
              {elapsedTime && (
                <div className="text-right">
                  <div className={`text-2xl font-bold ${
                    isCurrentPresentation3D || currentSession.survey_schedule_id ? 'text-purple-600' : 'text-[#49943d]'
                  }`}>
                    {String(elapsedTime.hours).padStart(2, '0')}:
                    {String(elapsedTime.minutes).padStart(2, '0')}:
                    {String(elapsedTime.seconds).padStart(2, '0')}
                  </div>
                  <p className="text-xs text-gray-500">Tempo decorrido</p>
                </div>
              )}
            </div>

            {/* Informações do Cliente ou Projeto 3D */}
            <div className="bg-white rounded-lg p-3 mb-4 space-y-2">
              {currentSession.survey_schedule_id ? (
                <>
                  <p className="text-sm text-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Horas agendadas: {currentSession.scheduled_hours}h
                  </p>
                  {currentSession.client_name && (
                    <p className="text-sm text-gray-700 flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Cliente: {currentSession.client_name}
                    </p>
                  )}
                </>
              ) : (
                <>
                  {isCurrentPresentation3D && (
                    <div className="flex items-center gap-2 pb-1">
                      <span className="px-2.5 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800 rounded-full border border-purple-200">
                        🎨 Apresentação 3D
                      </span>
                    </div>
                  )}
                  {(currentSession.client_phone || currentAppointment?.client_phone) && (
                    <p className="text-sm text-gray-700 flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {currentSession.client_phone || currentAppointment?.client_phone}
                    </p>
                  )}
                  {(currentSession.client_address || currentAppointment?.client_address) && (
                    <p className="text-sm text-gray-700 flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5" />
                      {currentSession.client_address || currentAppointment?.client_address}
                    </p>
                  )}
                  <p className="text-sm text-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Duração prevista: {currentSession.scheduled_duration || currentAppointment?.duration_minutes || '-'} min
                  </p>
                  {(currentSession.scheduled_duration || currentAppointment?.duration_minutes) && elapsedTime && (
                    <p className={`text-sm font-medium flex items-center gap-2 ${
                      (elapsedTime.hours * 60 + elapsedTime.minutes) > (currentSession.scheduled_duration || currentAppointment?.duration_minutes)
                        ? 'text-red-600'
                        : 'text-green-600'
                    }`}>
                      {elapsedTime.hours > 0 && `${elapsedTime.hours}h `}{elapsedTime.minutes}min decorridos
                      {((elapsedTime.hours * 60 + elapsedTime.minutes) > (currentSession.scheduled_duration || currentAppointment?.duration_minutes)) && (
                        <span className="text-xs">(+{Math.round((elapsedTime.hours * 60 + elapsedTime.minutes) - (currentSession.scheduled_duration || currentAppointment?.duration_minutes))}min)</span>
                      )}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Botão Finalizar */}
            <div className="space-y-3">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Observações sobre o atendimento (opcional)..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={2}
              />
              <button
                onClick={() => setShowEndSessionModal(true)}
                disabled={endSessionMutation.isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                <Square className="w-5 h-5" />
                {endSessionMutation.isLoading ? 'Finalizando...' : 'Finalizar Atendimento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Histórico de Atendimentos do Dia (concluídos) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Atendimentos de Hoje (concluídos)
        </h2>

        {isLoadingSessions ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#49943d]"></div>
          </div>
        ) : (
          <>
            {sessions.filter((s) => s.status === 'COMPLETED').length === 0 ? (
              <p className="text-sm text-gray-500">Nenhum atendimento concluído hoje.</p>
            ) : (
              <div className="space-y-3">
                {sessions
                  .filter((s) => s.status === 'COMPLETED')
                  .map((session) => (
                    <div
                      key={session.id}
                      className="border border-gray-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                    >
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900">
                            {session.appointment_type === 'PRESENTATION_3D'
                              ? `🎨 Apresentação 3D: ${session.client_name}`
                              : session.client_name}
                          </p>
                          {session.appointment_type === 'PRESENTATION_3D' && (
                            <span className="px-2 py-0.5 text-[11px] font-semibold bg-purple-100 text-purple-800 rounded-full border border-purple-200">
                              Apresentação 3D
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600">
                          Início: {formatTime(session.session_start)} &nbsp;|&nbsp;
                          Fim: {session.session_end ? formatTime(session.session_end) : '-'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {session.scheduled_duration ? `Previsto: ${session.scheduled_duration}min` : 'Duração'}
                        </p>
                        <p className={`text-sm font-medium ${
                          session.scheduled_duration && session.actual_duration_minutes > session.scheduled_duration
                            ? 'text-red-600'
                            : session.scheduled_duration && session.actual_duration_minutes < session.scheduled_duration
                            ? 'text-green-600'
                            : 'text-gray-900'
                        }`}>
                          {formatDuration(session.actual_duration_minutes)}
                          {session.scheduled_duration && session.actual_duration_minutes !== session.scheduled_duration && (
                            <span className="text-xs ml-1">
                              ({session.actual_duration_minutes > session.scheduled_duration ? '+' : ''}{session.actual_duration_minutes - session.scheduled_duration}min)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Lista de Agendamentos do Dia */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Agendamentos de Hoje ({appointments.length})
        </h2>

        {isLoadingAppointments ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#49943d]"></div>
          </div>
        ) : appointments.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum agendamento para hoje</p>
          </div>
        ) : (
          <div className="space-y-3">
            {appointments.map((appointment) => {
              const isActive = appointment.has_active_session;
              const canStart = !isActive && appointment.status === 'SCHEDULED';
              const isPresentation = appointment.appointment_type === 'PRESENTATION_3D';
              const isProjeto3D = appointment.appointment_type === 'PROJETO_3D';

              return (
                <div
                  key={appointment.id}
                  className={`border rounded-lg p-4 transition-colors ${
                    isActive
                      ? isPresentation || isProjeto3D
                        ? 'border-purple-300 bg-purple-50/50'
                        : 'border-[#49943d] bg-[#f0f7f0]'
                      : isPresentation
                      ? 'border-purple-200 bg-purple-50/20 hover:bg-purple-50/40'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900">
                          {isProjeto3D
                            ? `Projeto 3D: ${appointment.survey_code || 'Sem código'}`
                            : isPresentation
                            ? `🎨 Apresentação 3D: ${appointment.client_name}`
                            : appointment.client_name}
                        </h3>
                        {isPresentation && (
                          <span className="px-2.5 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800 rounded-full border border-purple-200">
                            Apresentação 3D
                          </span>
                        )}
                        {isActive && (
                          <span className={`px-2 py-0.5 text-xs text-white rounded-full flex items-center gap-1 ${
                            isProjeto3D || isPresentation
                              ? 'bg-purple-600'
                              : 'bg-[#49943d]'
                          }`}>
                            <CheckCircle className="w-3 h-3" />
                            Em andamento
                          </span>
                        )}
                        {appointment.status === 'COMPLETED' && (
                          <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
                            Concluído
                          </span>
                        )}
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        {isProjeto3D ? (
                          <>
                            <p className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              {appointment.scheduled_hours}h agendadas
                              {appointment.worked_hours > 0 && ` (${appointment.worked_hours}h trabalhadas)`}
                            </p>
                            <p className="flex items-center gap-2">
                              <User className="w-4 h-4" />
                              Cliente: {appointment.client_name || '-'}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              {formatTime(appointment.appointment_date)} - {appointment.duration_minutes} min
                            </p>
                            {appointment.client_phone && (
                              <p className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                {appointment.client_phone}
                              </p>
                            )}
                            {appointment.client_address && (
                              <p className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 mt-0.5" />
                                <span className="line-clamp-2">{appointment.client_address}</span>
                              </p>
                            )}
                            {isPresentation && appointment.survey_id && (
                              <div className="flex items-center gap-3 pt-1 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/levantamentos/${appointment.survey_id}/ficha-orcamento`)}
                                  className="text-xs text-purple-700 hover:text-purple-900 flex items-center gap-1 font-medium underline"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Ficha de Orçamento
                                </button>
                                <button
                                  type="button"
                                  onClick={() => navigate(`/levantamentos?surveyId=${appointment.survey_id}`)}
                                  className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 underline"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Ver Levantamento
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Botão de ação */}
                  {canStart && (
                    <button
                      onClick={() => {
                        if (appointment.appointment_type === 'PROJETO_3D') {
                          startSessionMutation.mutate({ survey_schedule_id: appointment.id });
                        } else if (appointment.appointment_type === 'PRESENTATION_3D') {
                          startSessionMutation.mutate(
                            { appointment_id: appointment.id },
                            {
                              onSuccess: () => {
                                if (appointment.survey_id) {
                                  navigate(`/levantamentos/${appointment.survey_id}/ficha-orcamento`);
                                }
                              }
                            }
                          );
                        } else {
                          startSessionMutation.mutate({ appointment_id: appointment.id });
                        }
                      }}
                      disabled={startSessionMutation.isLoading}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 ${
                        isProjeto3D || isPresentation
                          ? 'bg-purple-600 hover:bg-purple-700'
                          : 'bg-[#49943d] hover:bg-[#3a7630]'
                      }`}
                    >
                      <Play className="w-4 h-4" />
                      {isProjeto3D
                        ? 'Iniciar Projeto 3D'
                        : isPresentation
                        ? 'Iniciar Apresentação'
                        : 'Iniciar Atendimento'}
                    </button>
                  )}

                  {/* Botão para concluir projeto 3D - só mostra para RECEPCAO_PROJETOS */}
                  {appointment.appointment_type === 'PROJETO_3D' && appointment.status !== 'COMPLETED' && user?.role === 'RECEPCAO_PROJETOS' && (
                    <button
                      onClick={() => {
                        setComplete3DSurvey(appointment);
                        setShowComplete3DModal(true);
                      }}
                      disabled={complete3DMutation.isLoading}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 mt-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <Flag className="w-4 h-4" />
                      {complete3DMutation.isLoading ? 'Concluindo...' : 'Concluir Projeto 3D'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de confirmação para finalizar atendimento */}
      <ConfirmModal
        show={showEndSessionModal}
        onConfirm={() => {
          if (currentSession) {
            endSessionMutation.mutate({
              session_id: currentSession.id,
              notes: notes || null
            });
          }
          setShowEndSessionModal(false);
          setNotes('');
        }}
        onCancel={() => {
          setShowEndSessionModal(false);
        }}
        title="Finalizar Atendimento"
        message="Tem certeza que deseja finalizar este atendimento?"
        confirmText="Finalizar"
        cancelText="Voltar"
        type="danger"
        isLoading={endSessionMutation.isLoading}
      />

      {/* Modal de confirmação para concluir projeto 3D */}
      <ConfirmModal
        show={showComplete3DModal}
        onConfirm={() => {
          if (complete3DSurvey) {
            complete3DMutation.mutate({ surveyId: complete3DSurvey.survey_id });
          }
          setShowComplete3DModal(false);
          setComplete3DSurvey(null);
        }}
        onCancel={() => {
          setShowComplete3DModal(false);
          setComplete3DSurvey(null);
        }}
        title="Concluir Projeto 3D"
        message="Tem certeza que deseja concluir o projeto 3D? Esta ação notificará o gerente para aprovação."
        confirmText="Concluir"
        cancelText="Voltar"
        type="success"
        isLoading={complete3DMutation.isLoading}
      />
    </div>
  );
};

export default AppointmentSessions;

